package org.example.project.enginepersonal

import kotlinx.serialization.Serializable
import kotlinx.serialization.builtins.ListSerializer
import kotlinx.serialization.json.Json
import org.example.project.manager.LumaticaApiManager
import org.example.project.service.GlobalManager
import org.example.project.service.LoginService
import org.example.project.setup.SetupNodeApi
import org.example.project.utils.All

private val personalJson = Json {
    ignoreUnknownKeys = true
    encodeDefaults = false
}

private fun personalApiPart(value: String): String =
    js("encodeURIComponent")(if (value.isBlank()) "~" else value) as String

@Serializable
data class PersonalAkte(
    val userId: String = "",
    val qrCodeText: String = "",
    val qrCode: String = "",
    val qrLabel: String = "",
    val personal: Map<String, String> = emptyMap(),
    val vertrag: Map<String, String> = emptyMap(),
    val lohnbuchhaltung: Map<String, String> = emptyMap(),
    val bank: Map<String, String> = emptyMap(),
    val notfallkontakte: List<PersonalAkteRow> = emptyList(),
    val gehalt: List<PersonalAkteRow> = emptyList(),
    val beschaeftigungsverlauf: List<PersonalAkteRow> = emptyList(),
    val bonus: List<PersonalAkteRow> = emptyList(),
    val benefits: List<PersonalAkteRow> = emptyList()
)

@Serializable
data class PersonalAkteRow(
    val id: String = "",
    val values: Map<String, String> = emptyMap()
)

object PersonalakteApi {
    private fun firmenId(): String =
        LoginService.getUser()?.firmenId.orEmpty().trim()
            .ifBlank { GlobalManager.currentFirmenId.trim() }

    suspend fun loadUsers(art: String): List<All> {
        val companyId = firmenId()
        val route = when {
            art.equals("CUS", ignoreCase = true) && companyId.isNotBlank() ->
                "userverwaltung/company-users/${personalApiPart(companyId)}"
            companyId.isNotBlank() ->
                "userverwaltung/company-users/${personalApiPart(companyId)}"
            else -> "userverwaltung"
        }
        val raw = runCatching { LumaticaApiManager.call(route) }.getOrDefault("")
        if (raw.isBlank() || raw == "null") return emptyList()
        return runCatching { personalJson.decodeFromString(ListSerializer(All.AllNames.serializer()), raw) }
            .getOrDefault(emptyList())
    }

    suspend fun loadActiveLocations(): List<All> {
        val companyId = firmenId()
        if (companyId.isBlank()) return emptyList()
        return runCatching { SetupNodeApi.loadStandorte(companyId) }
            .getOrDefault(emptyList())
            .filter { it.aktiv }
    }

    suspend fun loadAkte(userId: String): PersonalAkte {
        if (userId.isBlank()) return PersonalAkte()
        val raw = runCatching {
            LumaticaApiManager.call("personalakte/load/${personalApiPart(firmenId())}/${personalApiPart(userId)}")
        }.getOrDefault("")
        if (raw.isBlank() || raw == "null") return PersonalAkte(userId = userId)
        return runCatching { personalJson.decodeFromString(PersonalAkte.serializer(), raw) }
            .getOrDefault(PersonalAkte(userId = userId))
    }

    suspend fun saveAkte(akte: PersonalAkte): Boolean {
        if (akte.userId.isBlank()) return false
        val body = personalJson.encodeToString(PersonalAkte.serializer(), akte)
        val raw = runCatching {
            // A personal file includes tables and a QR image.  Sending this
            // complete JSON document in a GET URL exceeds common URL limits,
            // so saves can fail silently (for example when only an address is
            // changed).  The API has a dedicated POST endpoint for it.
            LumaticaApiManager.postJson(
                "personalakte/save/${personalApiPart(firmenId())}/${personalApiPart(akte.userId)}",
                body
            )
        }.getOrDefault("")
        val saved = raw.contains("\"ok\":true") || raw.contains("\"ok\": true")
        if (saved) {
            val userPart = akte.userId.trim().ifBlank { "~" }
            GlobalManager.markJsonChanged("personal", "user/$userPart/akte/personalakte.json")
            GlobalManager.markJsonChanged("user", "user/$userPart/user.json")
            if (akte.qrCodeText.isNotBlank() || akte.qrCode.isNotBlank() || akte.qrLabel.isNotBlank()) {
                GlobalManager.markJsonChanged("personal", "user/$userPart/qr/qr.json")
            }
        }
        return saved
    }
}
